import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import { Check, X, Clock } from 'lucide-react';
import { SettingsLayout } from '../../../../design-system/components/layouts';
import {
  PageHeader,
  Badge,
  Button,
  Card,
  CardHeader,
  CardBody,
  DescriptionList,
  DataTable,
  CopyToClipboard,
  ConfirmDialog,
} from '../../../../design-system/components';
import { formatDateTime } from '../../../../lib/formatters';
import {
  admin_settings_webhooks_path,
  edit_admin_settings_webhook_path,
  admin_settings_webhook_path,
  toggle_active_admin_settings_webhook_path,
} from '../../../../lib/routes';
const eventStatusVariant = {
  delivered: 'success',
  failed: 'danger',
  pending: 'warning',
};
const eventColumns = [
  {
    id: 'status_icon',
    header: '',
    render: (row) => {
      if (row.status === 'delivered') {
        return <Check size={20} className="text-success-500" />;
      }
      if (row.status === 'failed') {
        return <X size={20} className="text-danger-500" />;
      }
      return <Clock size={20} className="text-warning-500" />;
    },
  },
  {
    id: 'event_type',
    header: 'Event',
    sortable: true,
    render: (row) => <span className="font-mono text-sm">{row.event_type}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    render: (row) => (
      <Badge variant={eventStatusVariant[row.status] ?? 'default'} size="sm">
        {row.status}
      </Badge>
    ),
  },
  {
    id: 'response_code',
    header: 'Response',
    render: (row) => {
      if (row.response_code === null) return <span className="text-fg-tertiary">—</span>;
      const isOk = row.response_code >= 200 && row.response_code < 300;
      return (
        <span
          className={
            isOk ? 'font-mono text-sm text-success-600' : 'font-mono text-sm text-danger-600'
          }
        >
          {row.response_code}
        </span>
      );
    },
  },
  {
    id: 'attempts',
    header: 'Attempts',
    render: (row) => row.attempts,
  },
  {
    id: 'created_at',
    header: 'Date',
    sortable: true,
    render: (row) => formatDateTime(row.created_at),
  },
];
export default function Show({ webhook, secret_visible }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [optimisticActive, setOptimisticActive] = useState(null);
  const displayActive = optimisticActive !== null ? optimisticActive : webhook.active;
  function handleToggleActive() {
    setOptimisticActive(!displayActive);
    router.post(
      toggle_active_admin_settings_webhook_path(webhook.id),
      {},
      {
        preserveScroll: true,
        onFinish: () => setOptimisticActive(null),
      },
    );
  }
  const events = Array.isArray(webhook.events) ? webhook.events : [];
  const details = [
    {
      term: 'URL',
      detail: <span className="font-mono text-sm break-all">{webhook.url}</span>,
    },
    {
      term: 'Status',
      detail: (
        <Badge variant={displayActive ? 'success' : 'default'}>
          {displayActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      term: 'Subscribed Events',
      detail: (
        <div className="flex flex-wrap gap-1">
          {events.length === 0 ? (
            <span className="text-fg-tertiary">None</span>
          ) : (
            events.map((event) => (
              <Badge key={event} variant="info" size="sm">
                {event}
              </Badge>
            ))
          )}
        </div>
      ),
    },
    {
      term: 'Secret',
      detail: secret_visible ? (
        <CopyToClipboard text={webhook.secret || ''}>
          <div className="flex items-center gap-2">
            <code className="rounded bg-surface-muted px-2 py-1 text-sm">{webhook.secret}</code>
          </div>
        </CopyToClipboard>
      ) : (
        <span className="font-mono text-sm text-fg-tertiary">whsec_••••••••••••</span>
      ),
    },
    {
      term: 'Created',
      detail: formatDateTime(webhook.created_at),
    },
    {
      term: 'Total Deliveries',
      detail: webhook.event_count,
    },
  ];
  return (
    <SettingsLayout>
      <PageHeader
        title="Webhook Endpoint"
        description={webhook.url}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleToggleActive}>
              {displayActive ? 'Deactivate' : 'Activate'}
            </Button>
            <Link href={edit_admin_settings_webhook_path(webhook.id)}>
              <Button variant="outline">Edit</Button>
            </Link>
            <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
              Delete
            </Button>
            <Link href={admin_settings_webhooks_path()}>
              <Button variant="ghost">Back</Button>
            </Link>
          </div>
        }
      />

      <div className="mt-6">
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-fg">Endpoint Details</h3>
          </CardHeader>
          <CardBody>
            <DescriptionList items={details} columns={2} />
          </CardBody>
        </Card>
      </div>

      <div className="mt-6">
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-fg">
              Recent Deliveries ({webhook.recent_events?.length ?? 0})
            </h3>
          </CardHeader>
          <CardBody className="p-0">
            <DataTable
              columns={eventColumns}
              data={webhook.recent_events ?? []}
              keyAccessor="id"
              emptyTitle="No delivery events yet"
              emptyDescription="Events will appear here once webhooks are triggered."
              className="border-0 rounded-none"
            />
          </CardBody>
        </Card>
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => router.delete(admin_settings_webhook_path(webhook.id))}
        title="Delete Webhook Endpoint"
        message="Are you sure you want to delete this webhook endpoint? All delivery history will be lost. This action cannot be undone."
        confirmLabel="Delete"
        destructive
      />
    </SettingsLayout>
  );
}
